import { api } from './api';
import type {
  FamilyMember,
  Family,
  FamilyMembersResponse,
  CreateFamilyMemberRequest,
  UpdateFamilyMemberRequest,
  LinkUserRequest,
  GetFamilyMembersParams,
  UserWithMember,
  UpdateUserRoleRequest,
} from '../types/members';
import type { ApiResponse } from '../types/index';
import type { BackendUserRole, UserStatus } from '../types/user';

// Family Members API endpoints
export const membersService = {
  // Get all family members with optional search and filtering
  async getFamilyMembers(params?: GetFamilyMembersParams): Promise<FamilyMembersResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.includeDeceased) searchParams.set('includeDeceased', 'true');
    if (params?.directoryOnly !== undefined) searchParams.set('directoryOnly', String(params.directoryOnly));

    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get<ApiResponse<FamilyMembersResponse>>(`/family-members${query}`);
    return response.data!;
  },

  // Get a single family member by ID
  async getFamilyMember(id: string): Promise<{ member: FamilyMember }> {
    const response = await api.get<ApiResponse<{ member: FamilyMember }>>(`/family-members/${id}`);
    return response.data!;
  },

  // Create a new family member (admin only)
  async createFamilyMember(data: CreateFamilyMemberRequest): Promise<{ member: FamilyMember; message: string }> {
    const response = await api.post<ApiResponse<{ member: FamilyMember }>>('/family-members', data);
    return { member: response.data!.member, message: response.message || 'Family member created' };
  },

  // Update an existing family member
  async updateFamilyMember(id: string, data: UpdateFamilyMemberRequest): Promise<{ member: FamilyMember }> {
    const response = await api.put<ApiResponse<{ member: FamilyMember }>>(`/family-members/${id}`, data);
    return response.data!;
  },

  // Delete a family member (admin only)
  async deleteFamilyMember(id: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<void>>(`/family-members/${id}`);
    return { message: response.message || 'Family member deleted' };
  },

  // Bulk delete family members (admin only)
  async bulkDeleteFamilyMembers(memberIds: string[]): Promise<{ message: string; deletedCount: number; deletedMemberIds: string[] }> {
    const response = await api.post<ApiResponse<{ deletedCount: number; deletedMemberIds: string[] }>>('/family-members/bulk-delete', { memberIds });
    return { ...response.data!, message: response.message || 'Family members deleted' };
  },

  // Link a user account to a family member (admin only)
  async linkUserToMember(memberId: string, data: LinkUserRequest): Promise<{ member: FamilyMember; message: string }> {
    const response = await api.put<ApiResponse<{ member: FamilyMember }>>(`/family-members/${memberId}/link-user`, data);
    return { member: response.data!.member, message: response.message || 'User linked' };
  },

  // Unlink a user from a family member (admin only)
  async unlinkUserFromMember(memberId: string): Promise<{ member: FamilyMember; message: string }> {
    const response = await api.put<ApiResponse<{ member: FamilyMember }>>(`/family-members/${memberId}/unlink-user`);
    return { member: response.data!.member, message: response.message || 'User unlinked' };
  },

  // Get all families
  async getFamilies(): Promise<{ families: Family[] }> {
    const response = await api.get<ApiResponse<{ families: Family[] }>>('/families');
    return response.data!;
  },
};

export interface BackendUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: BackendUserRole;
  status: UserStatus;
  emailVerified?: boolean;
  createdAt: string;
  updatedAt: string;
}

// User Management API endpoints
export const usersService = {
  // Get all users with pagination and filtering
  async getUsers(params?: { limit?: number; offset?: number; search?: string }): Promise<{ users: BackendUser[]; pagination: any }> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get<ApiResponse<{ users: BackendUser[]; pagination: any }>>(`/users${query}`);
    return response.data!;
  },

  // Create a new user (admin only)
  async createUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    role?: BackendUserRole;
    middleName?: string;
    password?: string;
  }): Promise<{ user: BackendUser; message: string; generatedPassword?: string }> {
    const response = await api.post<ApiResponse<{ user: BackendUser; generatedPassword?: string }>>('/users', userData);
    return { user: response.data!.user, message: response.message || 'User created', generatedPassword: response.data!.generatedPassword };
  },

  // Get user with their linked family member info
  async getUserWithMember(userId: string): Promise<UserWithMember> {
    const response = await api.get<ApiResponse<UserWithMember>>(`/users/${userId}/with-member`);
    return response.data!;
  },

  // Update a user (admin only)
  async updateUser(userId: string, userData: {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    email?: string;
    role?: BackendUserRole;
    status?: UserStatus;
    emailVerified?: boolean;
  }): Promise<{ user: BackendUser; message: string }> {
    const response = await api.put<ApiResponse<{ user: BackendUser }>>(`/users/${userId}`, userData);
    return { user: response.data!.user, message: response.message || 'User updated' };
  },

  // Update a user's role (admin only)
  async updateUserRole(userId: string, data: UpdateUserRoleRequest): Promise<{ user: UserWithMember['user']; message: string }> {
    const response = await api.put<ApiResponse<{ user: UserWithMember['user'] }>>(`/users/${userId}/role`, data);
    return { user: response.data!.user, message: response.message || 'Role updated' };
  },

  // Delete a user (admin only)
  async deleteUser(userId: string): Promise<{ message: string; deletedUserId: string }> {
    const response = await api.delete<ApiResponse<{ deletedUserId: string }>>(`/users/${userId}`);
    return { deletedUserId: response.data!.deletedUserId, message: response.message || 'User deleted' };
  },
};

// Combined service exports
export default {
  members: membersService,
  users: usersService,
};
